import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trophy, Search, MapPin, Calendar, Users, Infinity as InfinityIcon, Sparkles, X, ChevronRight, ChevronLeft, Phone, CreditCard, ShieldCheck, Eye } from 'lucide-react';
import { API_URL as API } from '../config/api';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import TournamentLeaderboard from '../components/TournamentLeaderboard';

const SPORTS = ['All', 'Football', 'Cricket', 'Badminton', 'Tennis', 'Basketball'];
const STATUSES = ['All', 'Live', 'Upcoming', 'Completed'];
const ITEMS_PER_PAGE = 9;

const DEFAULT_TOURNAMENT_BANNER = 'https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800&auto=format&fit=crop&q=80';

export function getTournamentImage(url) {
  if (!url || typeof url !== 'string' || !url.trim()) {
    return DEFAULT_TOURNAMENT_BANNER;
  }
  const clean = url.trim();
  if (clean.startsWith('http://') || clean.startsWith('https://') || clean.startsWith('data:')) {
    return clean;
  }
  const apiBase = API.replace(/\/api\/?$/, '');
  const path = clean.startsWith('/') ? clean : `/${clean}`;
  return `${apiBase}${path}`;
}

export default function Tournaments() {
  const { colors } = useTheme();
  const { user, token } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sportFilter, setSportFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef(null);

  // Registration Modal State
  const [registeringTournament, setRegisteringTournament] = useState(null);
  const [feeBreakdown, setFeeBreakdown] = useState(null);
  const [regForm, setRegForm] = useState({
    teamName: '',
    captainName: '',
    captainPhone: '',
    captainEmail: '',
    city: '',
    alternatePhone: '',
    additionalContact: '',
    playerCount: 5,
    playerNames: ''
  });
  const [submittingReg, setSubmittingReg] = useState(false);
  const [toast, setToast] = useState(null);

  // Track if current user is already registered for the selected tournament
  const [myRegistration, setMyRegistration] = useState(null); // null = unknown / not registered
  const [cancellingReg, setCancellingReg] = useState(false);

  // Handle PayU redirect query params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const regStatus = params.get('registration');
    if (regStatus === 'success') {
      setToast({ msg: '🎉 Payment Successful! Your team registration is confirmed.', type: 'success' });
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (regStatus === 'failed' || regStatus === 'cancelled' || regStatus === 'cancel') {
      setToast({ msg: '❌ Payment cancelled or unsuccessful. Your registration was not completed.', type: 'error' });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchFeeBreakdown = async (tId) => {
    try {
      const res = await axios.get(`${API}/tournaments/${tId}/calculate-fee`);
      setFeeBreakdown(res.data);
    } catch (err) {
      console.error('Failed to calculate fee:', err);
    }
  };

  const formatPhoneInput = (val) => {
    if (!val) return '';
    let str = val.toString().trim();
    if (str.startsWith('+91')) {
      str = str.slice(3).trim();
    } else if (str.startsWith('+')) {
      str = str.slice(1).trim();
    }
    let digits = str.replace(/\D/g, '');
    if (digits.length > 10 && digits.startsWith('91')) {
      digits = digits.slice(2);
    }
    digits = digits.replace(/^[^6-9]+/, '');
    digits = digits.slice(0, 10);
    if (!digits) return '';
    return `+91 ${digits}`;
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (!token || !user) {
      alert('Please log in to register your team!');
      return;
    }
    // Guard: if registration closed, abort
    if (registeringTournament?.registration_close_date && new Date() > new Date(registeringTournament.registration_close_date)) {
      alert('Registration for this tournament has closed. You can no longer register.');
      setRegisteringTournament(null);
      return;
    }
    if (!regForm.teamName.trim() || !regForm.captainName.trim() || !regForm.captainPhone.trim()) {
      alert('Please fill all required fields: Team Name, Captain Name, and Captain Phone Number.');
      return;
    }
    // Captain Name Validation (No numbers or special characters)
    if (/[^a-zA-Z\s.-]/.test(regForm.captainName.trim())) {
      alert('Captain Name must only contain letters and spaces (no numbers or special characters).');
      return;
    }
    // Captain Phone Validation (Indian mobile starting digits 6,7,8,9)
    const phoneClean = regForm.captainPhone.trim().replace(/\s+/g, '');
    if (!/^(\+91)?[6-9]\d{9}$/.test(phoneClean)) {
      alert('Contact Phone Number must be a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9 (e.g. +91 9876543210).');
      return;
    }
    setSubmittingReg(true);
    try {
      const res = await axios.post(`${API}/tournaments/${registeringTournament._id}/payu-checkout`, {
        team_name: regForm.teamName.trim(),
        captain_name: regForm.captainName.trim(),
        captain_phone: regForm.captainPhone.trim(),
        captain_email: regForm.captainEmail.trim() || undefined,
        city: regForm.city.trim() || undefined,
        alternate_phone: regForm.alternatePhone.trim() || undefined,
        additional_contact: regForm.additionalContact.trim() || undefined,
        player_count: parseInt(regForm.playerCount) || 5,
        player_names: regForm.playerNames.trim() || undefined,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.data.free) {
        setToast({ msg: '🎉 Team registered successfully!', type: 'success' });
        setRegisteringTournament(null);
        // Refresh registration status so the button switches immediately
        const tId = registeringTournament?._id || selectedTournament?._id;
        if (tId && token) {
          axios.get(`${API}/tournaments/${tId}/my-registration`, {
            headers: { Authorization: `Bearer ${token}` }
          }).then(r => setMyRegistration(r.data)).catch(() => {});
        }
        fetchTournaments();
      } else if (res.data.action_url && res.data.params) {
        // Submit hidden form to PayU gateway
        const actionUrl = (res.data.action_url || '').replace('info.payu.in', 'secure.payu.in');
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = actionUrl;
        Object.entries(res.data.params).forEach(([k, v]) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = k;
          input.value = v !== null && v !== undefined ? String(v) : '';
          form.appendChild(input);
        });
        document.body.appendChild(form);
        form.submit();
      }
    } catch (err) {
      alert(err.response?.data?.msg || 'Registration failed.');
    } finally {
      setSubmittingReg(false);
    }
  };

  const fetchTournaments = async () => {
    setLoading(true);
    try {
      let url = `${API}/tournaments?`;
      if (sportFilter !== 'All') url += `sport=${encodeURIComponent(sportFilter)}&`;
      if (statusFilter !== 'All') url += `status=${encodeURIComponent(statusFilter.toLowerCase())}&`;

      const res = await axios.get(url);
      setTournaments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch tournaments:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTournaments();
    setCurrentPage(1);
  }, [sportFilter, statusFilter]);

  // When a tournament modal opens, check if current user is already registered
  useEffect(() => {
    setMyRegistration(null);
    if (!selectedTournament || !token || !user) return;
    axios.get(`${API}/tournaments/${selectedTournament._id}/my-registration`, {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then(res => setMyRegistration(res.data))
      .catch(() => setMyRegistration({ registered: false, registration: null }));
  }, [selectedTournament?._id, token]);

  // Close search dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter tournaments based on search query (Name, Date, Sport, Turf Name, Address)
  const filteredTournaments = tournaments.filter(t => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase().trim();
    const dateStr = t.start_date ? new Date(t.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }).toLowerCase() : '';
    const sportStr = (t.sport === 'Custom' ? t.sport_custom : t.sport) || '';
    
    return (t.name || '').toLowerCase().includes(q)
      || dateStr.includes(q)
      || sportStr.toLowerCase().includes(q)
      || (t.turf_name || '').toLowerCase().includes(q)
      || (t.address || '').toLowerCase().includes(q);
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredTournaments.length / ITEMS_PER_PAGE) || 1;
  const paginatedTournaments = filteredTournaments.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  // Autocomplete search suggestions (when 2+ letters typed)
  const searchSuggestions = searchQuery.trim().length >= 2 ? filteredTournaments.slice(0, 6) : [];

  return (
    <div style={{ background: colors.background, minHeight: '85vh', fontFamily: "'DM Sans', sans-serif", paddingBottom: '4rem' }}>
      
      {toast && (
        <div style={{
          position: 'fixed', top: '24px', right: '24px', zIndex: 999999,
          background: toast.type === 'success' ? '#084734' : '#DC2626',
          color: '#CEF17B', padding: '14px 22px', borderRadius: '14px',
          fontWeight: '800', fontSize: '0.92rem', boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
          display: 'flex', alignItems: 'center', gap: '10px'
        }}>
          <span>{toast.msg}</span>
          <button onClick={() => setToast(null)} style={{ background: 'none', border: 'none', color: '#CEF17B', cursor: 'pointer', padding: '2px' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* HERO BANNER */}
      <div style={{
        background: '#084734',
        padding: '3.5rem 1.5rem 3rem',
        color: 'white',
        textAlign: 'center',
        position: 'relative',
        zIndex: 100,
        overflow: 'visible'
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 2, maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(206,241,123,0.15)', color: '#CEF17B', padding: '6px 16px', borderRadius: '50px', fontSize: '0.85rem', fontWeight: '700', marginBottom: '1rem' }}>
            <Trophy size={16} /> TurfX Championship Hub
          </div>

          <h1 style={{ fontSize: '2.5rem', fontWeight: '900', margin: '0 0 0.5rem', fontFamily: "'Sora', sans-serif" }}>
            Active & Upcoming <span style={{ color: '#CEF17B' }}>Tournaments</span>
          </h1>

          <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', maxWidth: '580px', margin: '0 auto 2rem', fontWeight: '400' }}>
            Explore local leagues, knockout tournaments, and open championships near you. Register your team and play to win!
          </p>

          {/* Autocomplete Search Bar */}
          <div ref={searchRef} style={{ position: 'relative', zIndex: 101, maxWidth: '560px', margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', background: 'white', borderRadius: '50px', padding: '6px 6px 6px 18px', boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
              <Search size={18} color="#084734" style={{ marginRight: '10px', flexShrink: 0 }} />
              <input
                type="text"
                placeholder="Search tournament name, date, sport, or turf venue..."
                value={searchQuery}
                onChange={e => {
                  setSearchQuery(e.target.value);
                  setShowDropdown(true);
                  setCurrentPage(1);
                }}
                onFocus={() => setShowDropdown(true)}
                style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.92rem', color: '#111827', fontWeight: '500' }}
              />
              {searchQuery && (
                <button onClick={() => { setSearchQuery(''); setShowDropdown(false); setCurrentPage(1); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', padding: '4px', display: 'flex', alignItems: 'center' }}>
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Instant Autocomplete Suggestions Dropdown */}
            {showDropdown && searchSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '115%', left: 0, right: 0,
                background: 'white', borderRadius: '16px', border: '1.5px solid #E9EDE8',
                boxShadow: '0 20px 50px rgba(0,0,0,0.25)', zIndex: 99999, overflow: 'hidden', textAlign: 'left'
              }}>
                <div style={{ padding: '8px 16px', background: '#F8FAF7', borderBottom: '1px solid #EEF2E6', fontSize: '0.72rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase' }}>
                  Matching Tournaments ({searchSuggestions.length})
                </div>
                {searchSuggestions.map(item => (
                  <div
                    key={item._id}
                    onClick={() => {
                      setSelectedTournament(item);
                      setShowDropdown(false);
                    }}
                    style={{
                      padding: '12px 16px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer',
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      transition: 'all 0.15s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F0FDF4'}
                    onMouseLeave={e => e.currentTarget.style.background = 'white'}
                  >
                    <div>
                      <div style={{ fontWeight: '800', fontSize: '0.9rem', color: '#0D1F0F' }}>{item.name}</div>
                      <div style={{ fontSize: '0.78rem', color: '#6B7280', marginTop: '2px' }}>
                        📍 {item.turf_name} · 🗓️ {new Date(item.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </div>
                    </div>
                    <span style={{ fontSize: '0.72rem', fontWeight: '800', color: item.status === 'live' ? '#DC2626' : '#084734', background: item.status === 'live' ? '#FEE2E2' : '#DCEFB8', padding: '3px 8px', borderRadius: '12px' }}>
                      {item.status === 'live' ? '🔴 Live' : '🟢 Upcoming'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MAIN CONTAINER */}
      <div style={{ maxWidth: '1200px', margin: '2rem auto 0', padding: '0 1.5rem', position: 'relative', zIndex: 1 }}>
        
        {/* FILTERS */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem', background: '#fff', padding: '1rem 1.25rem', borderRadius: '16px', border: '1px solid #E9EDE8' }}>
          
          {/* Sports Filter */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', marginRight: '4px' }}>Sport:</span>
            {SPORTS.map(s => (
              <button
                key={s}
                onClick={() => setSportFilter(s)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '30px',
                  border: `1.5px solid ${sportFilter === s ? '#084734' : '#E5E7EB'}`,
                  background: sportFilter === s ? '#084734' : 'transparent',
                  color: sportFilter === s ? '#CEF17B' : '#374151',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Status Filter */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', marginRight: '4px' }}>Status:</span>
            {STATUSES.map(st => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                style={{
                  padding: '6px 14px',
                  borderRadius: '30px',
                  border: `1.5px solid ${statusFilter === st ? '#084734' : '#E5E7EB'}`,
                  background: statusFilter === st ? '#DCEFB8' : 'transparent',
                  color: statusFilter === st ? '#084734' : '#374151',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {st === 'Live' ? '🔴 Live' : st === 'Upcoming' ? '🟢 Upcoming' : st === 'Completed' ? '🏁 Completed' : st}
              </button>
            ))}
          </div>

        </div>

        {/* TOURNAMENTS GRID */}
        {loading ? (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6B7280', fontWeight: '600' }}>
            Loading tournaments...
          </div>
        ) : paginatedTournaments.length > 0 ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '1.5rem' }}>
              {paginatedTournaments.map(t => {
                const formatStr = t.tournament_type === 'Custom' ? t.tournament_type_custom : t.tournament_type;
                const sportStr = t.sport === 'Custom' ? t.sport_custom : t.sport;
                const isLive = t.status === 'live';
                const startDate = t.start_date ? new Date(t.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
                const endDate = t.end_date ? new Date(t.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

                return (
                  <div
                    key={t._id}
                    onClick={() => setSelectedTournament(t)}
                    style={{
                      background: 'white',
                      borderRadius: '20px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      border: '1.5px solid #E9EDE8',
                      cursor: 'pointer',
                      transition: 'all 0.25s',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
                      position: 'relative'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-4px)';
                      e.currentTarget.style.boxShadow = '0 12px 32px rgba(8,71,52,0.12)';
                      e.currentTarget.style.borderColor = '#084734';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.03)';
                      e.currentTarget.style.borderColor = '#E9EDE8';
                    }}
                  >
                    {/* Banner Image Header */}
                    <div style={{ position: 'relative', width: '100%', height: '170px', overflow: 'hidden', background: 'linear-gradient(135deg, #084734 0%, #062C21 100%)' }}>
                      <img
                        src={getTournamentImage(t.image_url)}
                        alt={t.name}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_TOURNAMENT_BANNER;
                        }}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      {/* Floating Badges */}
                      <div style={{ position: 'absolute', top: '12px', left: '12px', right: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
                        <span style={{
                          background: isLive ? '#DC2626' : '#084734',
                          color: isLive ? '#FFF' : '#CEF17B',
                          fontSize: '0.72rem',
                          fontWeight: '800',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          letterSpacing: '0.5px',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}>
                          {isLive ? '🔴 LIVE NOW' : t.status === 'completed' ? '🏁 COMPLETED' : '🟢 UPCOMING'}
                        </span>
                        <span style={{ fontSize: '0.75rem', fontWeight: '800', color: '#084734', background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(4px)', padding: '4px 10px', borderRadius: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                          {sportStr}
                        </span>
                      </div>
                    </div>

                    <div style={{ padding: '1.4rem', display: 'flex', flexDirection: 'column', flex: 1, justifyContent: 'space-between' }}>
                      <div>
                        {/* Title */}
                        <h3 style={{ fontSize: '1.3rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '10px', lineHeight: 1.3 }}>
                          {t.name}
                        </h3>

                        {/* Details */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.85rem', color: '#4B5563', marginBottom: '1.25rem' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                            <MapPin size={16} color="#084734" style={{ marginTop: '2px', flexShrink: 0 }} />
                            <div>
                              <strong style={{ color: '#111827' }}>{t.turf_name}</strong>
                              <div style={{ fontSize: '0.78rem', color: '#6B7280' }}>{t.address}</div>
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: '#F8FAF7', padding: '8px 12px', borderRadius: '10px', border: '1px solid #EEF2E6' }}>
                            <div style={{ fontSize: '0.8rem', color: '#161616', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <Calendar size={14} color="#084734" />
                              <span><strong>Starts From:</strong> {startDate}</span>
                            </div>
                            {t.registration_close_date && (
                              <div style={{ fontSize: '0.78rem', color: '#DC2626', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '20px' }}>
                                <span><strong>Reg Closes:</strong> {new Date(t.registration_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                              </div>
                            )}
                            {endDate && (
                              <div style={{ fontSize: '0.8rem', color: '#4B5563', display: 'flex', alignItems: 'center', gap: '6px', paddingLeft: '20px' }}>
                                <span><strong>Ends On:</strong> {endDate}</span>
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Users size={16} color="#084734" style={{ flexShrink: 0 }} />
                            <span>Format: <strong>{formatStr}</strong> ({t.team_size}v{t.team_size})</span>
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🎂 <strong>Age Limit:</strong> {t.is_open_age || (!t.min_age && !t.max_age) ? 'Open for All Ages' : `${t.min_age || 0} - ${t.max_age || 'Any'} Years`}</span>
                          </div>
                        </div>

                        {/* Prize Banner */}
                        {t.prize_pool && (
                          <div style={{ background: '#FEF3C7', color: '#D97706', padding: '8px 12px', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '800', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Trophy size={16} /> Prize Pool: {t.prize_pool}
                          </div>
                        )}
                      </div>

                      {/* Footer Stats & View Details Button */}
                      <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Entry Fee</div>
                            <div style={{ fontSize: '1.1rem', fontWeight: '900', color: '#084734' }}>
                              {t.entry_fee > 0 ? `₹${t.entry_fee}` : 'FREE ENTRY'}
                            </div>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.7rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Teams Limit</div>
                            <div style={{ fontSize: '0.92rem', fontWeight: '800', color: '#111827' }}>
                              {t.is_unlimited_teams ? 'Unlimited (∞)' : `${t.max_teams} Teams`}
                            </div>
                          </div>
                        </div>

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTournament(t);
                          }}
                          style={{
                            width: '100%',
                            padding: '10px 16px',
                            borderRadius: '12px',
                            background: '#084734',
                            color: '#FFFFFF',
                            border: 'none',
                            fontWeight: '800',
                            fontSize: '0.88rem',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            boxShadow: '0 4px 12px rgba(8,71,52,0.15)',
                            transition: 'all 0.2s'
                          }}
                        >
                          <Eye size={16} /> View Details
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* PAGINATION BAR (9 Cards per Page) */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '2.5rem' }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => {
                    setCurrentPage(prev => Math.max(prev - 1, 1));
                    window.scrollTo({ top: 300, behavior: 'smooth' });
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '10px 18px', borderRadius: '12px', border: '1.5px solid #084734',
                    background: currentPage === 1 ? '#F3F4F6' : '#084734',
                    color: currentPage === 1 ? '#9CA3AF' : '#CEF17B',
                    fontWeight: '800', fontSize: '0.88rem', cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  <ChevronLeft size={18} /> Previous
                </button>
                <span style={{ fontSize: '0.9rem', fontWeight: '800', color: '#1F2937' }}>
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => {
                    setCurrentPage(prev => Math.min(prev + 1, totalPages));
                    window.scrollTo({ top: 300, behavior: 'smooth' });
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                    padding: '10px 18px', borderRadius: '12px', border: '1.5px solid #084734',
                    background: currentPage === totalPages ? '#F3F4F6' : '#084734',
                    color: currentPage === totalPages ? '#9CA3AF' : '#CEF17B',
                    fontWeight: '800', fontSize: '0.88rem', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next <ChevronRight size={18} />
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ background: '#fff', padding: '4rem 2rem', borderRadius: '24px', border: '1.5px dashed #D1D5DB', textAlign: 'center' }}>
            <Trophy size={48} color="#9CA3AF" style={{ marginBottom: '1rem' }} />
            <h3 style={{ fontSize: '1.2rem', fontWeight: '800', color: '#1F2937', marginBottom: '4px' }}>No Tournaments Found</h3>
            <p style={{ color: '#6B7280', fontSize: '0.9rem' }}>Try adjusting your filters or search terms.</p>
          </div>
        )}
      </div>

      {/* TOURNAMENT DETAIL MODAL */}
      {selectedTournament && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)', zIndex: 10000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '640px', width: '100%',
            maxHeight: '90vh', overflowY: 'auto', padding: '2rem', position: 'relative',
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)'
          }}>
            <button
              onClick={() => setSelectedTournament(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#374151' }}
            >
              <X size={20} />
            </button>

            <img
              src={getTournamentImage(selectedTournament.image_url)}
              alt={selectedTournament.name}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = DEFAULT_TOURNAMENT_BANNER;
              }}
              style={{ width: '100%', height: '220px', objectFit: 'cover', borderRadius: '16px', marginBottom: '1.25rem' }}
            />

            <div style={{ display: 'inline-block', background: '#DCEFB8', color: '#084734', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '800', textTransform: 'uppercase', marginBottom: '8px' }}>
              {selectedTournament.sport === 'Custom' ? selectedTournament.sport_custom : selectedTournament.sport}
            </div>

            <h2 style={{ fontSize: '1.6rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '0.5rem', lineHeight: 1.2 }}>
              {selectedTournament.name}
            </h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '0.9rem', color: '#4B5563', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={18} color="#084734" />
                <span style={{ fontWeight: '700', color: '#1F2937' }}>{selectedTournament.turf_name}</span> · {selectedTournament.address}
              </div>
              {selectedTournament.start_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={18} color="#084734" />
                  <span><strong>Starts From:</strong> {new Date(selectedTournament.start_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {selectedTournament.registration_close_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#DC2626', fontWeight: '600' }}>
                  <Calendar size={18} color="#DC2626" />
                  <span><strong>Registration Closes:</strong> {new Date(selectedTournament.registration_close_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              {selectedTournament.end_date && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Calendar size={18} color="#084734" />
                  <span><strong>Ends On:</strong> {new Date(selectedTournament.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Users size={18} color="#084734" />
                <span><strong>Format:</strong> {selectedTournament.tournament_type === 'Custom' ? selectedTournament.tournament_type_custom : selectedTournament.tournament_type} · {selectedTournament.team_size} Players per team</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InfinityIcon size={18} color="#084734" />
                <span><strong>Max Teams Allowed:</strong> {selectedTournament.is_unlimited_teams ? 'Unlimited Teams (∞)' : `${selectedTournament.max_teams} Teams`}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>🎂 <strong>Age Limit:</strong> {selectedTournament.is_open_age || (!selectedTournament.min_age && !selectedTournament.max_age) ? 'Open for All Ages' : `${selectedTournament.min_age || 0} - ${selectedTournament.max_age || 'Any'} Years`}</span>
              </div>
            </div>

            {selectedTournament.prize_pool && (
              <div style={{ background: '#FEF3C7', color: '#D97706', padding: '14px 16px', borderRadius: '16px', fontSize: '0.9rem', fontWeight: '800', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '0.95rem' }}>
                  <Trophy size={20} /> <span>Prize Pool & Rewards:</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {selectedTournament.prize_pool.split(' | ').map((pz, idx) => (
                    <span key={idx} style={{ background: 'white', color: '#B45309', padding: '4px 12px', borderRadius: '8px', border: '1px solid #FCD34D', fontSize: '0.82rem', fontWeight: '800', boxShadow: '0 2px 6px rgba(217,119,6,0.1)' }}>
                      {pz}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Organizer Contact Details Box */}
            <div style={{ background: '#F0FDF4', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid #BBF7D0', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', marginBottom: '8px', letterSpacing: '0.5px' }}>
                Organizer & Host Contact Information
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: '800', color: '#0D1F0F', fontSize: '0.95rem' }}>
                    {selectedTournament.organizer_name || selectedTournament.owner_name || 'Tournament Host'}
                  </div>
                  {selectedTournament.organizer_email && (
                    <div style={{ fontSize: '0.8rem', color: '#4B5563', marginTop: '2px' }}>
                      📧 {selectedTournament.organizer_email}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {(selectedTournament.organizer_phone || selectedTournament.owner_phone) && (
                    <a
                      href={`tel:${selectedTournament.organizer_phone || selectedTournament.owner_phone}`}
                      style={{ background: '#084734', color: '#CEF17B', textDecoration: 'none', padding: '8px 14px', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      📞 Call Host
                    </a>
                  )}

                  {selectedTournament.organizer_whatsapp && selectedTournament.organizer_whatsapp.trim() !== '' && selectedTournament.organizer_whatsapp.replace(/\D/g, '').length >= 10 && (
                    <a
                      href={`https://wa.me/${selectedTournament.organizer_whatsapp.replace(/[^\d]/g, '')}?text=${encodeURIComponent(`Hi! I am interested in registering for "${selectedTournament.name}"`)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ background: '#25D366', color: 'white', textDecoration: 'none', padding: '8px 14px', borderRadius: '10px', fontSize: '0.82rem', fontWeight: '800', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                    >
                      💬 WhatsApp Chat
                    </a>
                  )}
                </div>
              </div>
            </div>

            {selectedTournament.description && (
              <div style={{ marginBottom: '1.5rem' }}>
                <h4 style={{ fontSize: '0.85rem', fontWeight: '800', color: '#374151', textTransform: 'uppercase', marginBottom: '6px' }}>Rules & Details</h4>
                <p style={{ fontSize: '0.9rem', color: '#4B5563', lineHeight: 1.6, whiteSpace: 'pre-line' }}>{selectedTournament.description}</p>
              </div>
            )}

            {(() => {
              const now = new Date();
              const isRegCloseExceeded = selectedTournament.registration_close_date && now > new Date(selectedTournament.registration_close_date);
              const isStartExceeded = selectedTournament.start_date && now > new Date(selectedTournament.start_date);
              const regCount = selectedTournament.registered_count || selectedTournament.teams_count || selectedTournament.team_count || 0;
              const isMaxTeamsFilled = !selectedTournament.is_unlimited_teams && selectedTournament.max_teams && regCount >= selectedTournament.max_teams;

              const isClosed = isRegCloseExceeded || isStartExceeded || isMaxTeamsFilled;
              const isCompleted = selectedTournament.status === 'completed';
              const isCancelled = selectedTournament.status === 'cancelled';
              const alreadyRegistered = myRegistration?.registered;
              const regEntry = myRegistration?.registration;

              return (
                <div style={{ borderTop: '1px solid #E9EDE8', paddingTop: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6B7280', fontWeight: '700', textTransform: 'uppercase' }}>Entry Fee</div>
                    <div style={{ fontSize: '1.4rem', fontWeight: '900', color: '#084734' }}>
                      {selectedTournament.entry_fee > 0 ? `₹${selectedTournament.entry_fee}` : 'FREE ENTRY'}
                    </div>
                  </div>

                  {alreadyRegistered ? (
                    /* Already registered — show team info + Cancel button */
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                      <div style={{ background: '#DCEFB8', color: '#084734', padding: '5px 14px', borderRadius: '20px', fontWeight: '800', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        ✅ Registered: <strong>{regEntry?.team_name}</strong>
                      </div>
                      {!isCompleted && !isCancelled && (
                        <>
                        <button
                          type="button"
                          disabled={cancellingReg}
                          onClick={async () => {
                            if (!window.confirm(`Cancel registration for "${regEntry?.team_name}"? This may not be refundable after 24 hours.`)) return;
                            setCancellingReg(true);
                            try {
                              const res = await axios.post(`${API}/tournaments/registrations/${regEntry._id}/cancel`, {}, {
                                headers: { Authorization: `Bearer ${token}` }
                              });
                              setMyRegistration({ registered: false, registration: null });
                              setToast({ msg: res.data?.msg || '✅ Registration cancelled and refund initiated successfully.', type: 'success' });
                            } catch (err) {
                              setToast({ msg: err?.response?.data?.detail || 'Failed to cancel registration.', type: 'error' });
                            } finally {
                              setCancellingReg(false);
                            }
                          }}
                          style={{
                            background: cancellingReg ? '#F3F4F6' : '#FEE2E2',
                            color: cancellingReg ? '#9CA3AF' : '#DC2626',
                            border: '1.5px solid #FECACA',
                            padding: '9px 18px', borderRadius: '50px',
                            fontWeight: '800', fontSize: '0.88rem',
                            cursor: cancellingReg ? 'wait' : 'pointer',
                            display: 'inline-flex', alignItems: 'center', gap: '6px'
                          }}
                        >
                          {cancellingReg ? 'Cancelling...' : '✕ Cancel Registration'}
                        </button>
                        <div style={{ fontSize: '0.7rem', color: '#9CA3AF', textAlign: 'right', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span>⚠️</span>
                          <span>Refund only if cancelled within <strong>24 hours</strong> of registration</span>
                         </div>
                        </>
                      )}
                    </div>
                  ) : (
                    /* Not yet registered */
                    <button
                      type="button"
                      disabled={isClosed || isCompleted || isCancelled}
                      onClick={() => {
                        if (isClosed || isCompleted || isCancelled) return;
                        setRegisteringTournament(selectedTournament);
                        setRegForm({
                          teamName: '',
                          captainName: user?.name || '',
                          captainPhone: user?.phone || '',
                          captainEmail: user?.email || '',
                          city: selectedTournament.city || '',
                          alternatePhone: '',
                          additionalContact: '',
                          playerCount: selectedTournament.max_team_size || selectedTournament.team_size || 5,
                          playerNames: ''
                        });
                        fetchFeeBreakdown(selectedTournament._id);
                      }}
                      style={{
                        background: (isClosed || isCompleted || isCancelled) ? '#9CA3AF' : '#084734',
                        color: (isClosed || isCompleted || isCancelled) ? '#F3F4F6' : '#CEF17B',
                        border: 'none',
                        padding: '12px 24px', borderRadius: '50px', fontWeight: '800',
                        fontSize: '0.92rem', display: 'inline-flex', alignItems: 'center', gap: '8px',
                        cursor: (isClosed || isCompleted || isCancelled) ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isCancelled ? '❌ Tournament Cancelled' : isCompleted ? 'Tournament Ended' : isMaxTeamsFilled ? 'Registration Closed (Full)' : isClosed ? 'Registration Closed' : 'Register Team Now'}
                      {!isClosed && !isCompleted && !isCancelled && <ChevronRight size={18} />}
                    </button>
                  )}
                </div>
              );
            })()}

            {/* Leaderboard — shown for completed tournaments */}
            {selectedTournament.status === 'completed' && (
              <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: '1.5rem', marginTop: '0.5rem' }}>
                <TournamentLeaderboard tournamentId={selectedTournament._id} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* TOURNAMENT REGISTRATION FORM MODAL */}
      {registeringTournament && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)', zIndex: 100000,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem'
        }}>
          <div style={{
            background: 'white', borderRadius: '24px', maxWidth: '560px', width: '100%',
            maxHeight: '92vh', overflowY: 'auto', padding: '2rem', position: 'relative',
            boxShadow: '0 25px 60px rgba(0,0,0,0.3)', animation: 'fadeIn 0.2s ease-out'
          }}>
            <button
              onClick={() => setRegisteringTournament(null)}
              style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: '#F3F4F6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#374151' }}
            >
              <X size={20} />
            </button>

            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#DCEFB8', color: '#084734', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '800', marginBottom: '1rem' }}>
              <Trophy size={14} /> Team Registration
            </div>

            <h2 style={{ fontSize: '1.5rem', fontWeight: '900', color: '#0D1F0F', marginBottom: '0.4rem', lineHeight: 1.2 }}>
              {registeringTournament.name}
            </h2>
            <div style={{ fontSize: '0.85rem', color: '#6B7280', marginBottom: '1.5rem' }}>
              📍 {registeringTournament.turf_name} · Format: {registeringTournament.tournament_type === 'Custom' ? registeringTournament.tournament_type_custom : registeringTournament.tournament_type} ({registeringTournament.team_size}v{registeringTournament.team_size})
            </div>

            <form onSubmit={handleRegisterSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Team Name <span style={{ color: '#DC2626' }}>*</span>
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. RCB"
                  value={regForm.teamName}
                  onChange={e => setRegForm({ ...regForm, teamName: e.target.value })}
                  style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Captain Name <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="Full Name"
                    value={regForm.captainName}
                    onChange={e => setRegForm({ ...regForm, captainName: e.target.value.replace(/[^a-zA-Z\s.-]/g, '') })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Contact Phone <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    placeholder="e.g. +91 9876543210"
                    maxLength={14}
                    value={regForm.captainPhone}
                    onChange={e => setRegForm({ ...regForm, captainPhone: formatPhoneInput(e.target.value) })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Alternate Phone (Optional)
                  </label>
                  <input
                    type="tel"
                    placeholder="e.g. +91 9876543210"
                    maxLength={14}
                    value={regForm.alternatePhone}
                    onChange={e => setRegForm({ ...regForm, alternatePhone: formatPhoneInput(e.target.value) })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Additional Contact / Notes (Optional)
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Vice-captain contact or special notes"
                    value={regForm.additionalContact}
                    onChange={e => setRegForm({ ...regForm, additionalContact: e.target.value })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem' }}>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    placeholder="captain@email.com"
                    value={regForm.captainEmail}
                    onChange={e => setRegForm({ ...regForm, captainEmail: e.target.value })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    City / Location <span style={{ color: '#DC2626' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Bengaluru"
                    value={regForm.city}
                    onChange={e => setRegForm({ ...regForm, city: e.target.value })}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.92rem', fontWeight: '600', outline: 'none' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                    Number of Players 🔒
                  </label>
                  <input
                    type="number"
                    readOnly
                    disabled
                    value={regForm.playerCount}
                    style={{ width: '100%', padding: '12px 14px', borderRadius: '12px', border: '1.5px solid #E5E7EB', background: '#F3F4F6', color: '#6B7280', fontSize: '0.92rem', fontWeight: '800', cursor: 'not-allowed' }}
                  />
                  <div style={{ fontSize: '0.72rem', color: '#6B7280', marginTop: '4px', fontWeight: '600' }}>
                    Fixed by organizer ({registeringTournament.min_team_size && registeringTournament.max_team_size && registeringTournament.min_team_size !== registeringTournament.max_team_size ? `${registeringTournament.min_team_size} - ${registeringTournament.max_team_size} Players Range` : `${registeringTournament.team_size || 5} Players`})
                  </div>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.82rem', fontWeight: '800', color: '#374151', display: 'block', marginBottom: '6px' }}>
                  Player Names / Roster (Optional)
                </label>
                <textarea
                  rows="2"
                  placeholder="Enter player names separated by commas (e.g. John, Rahul, Alex)"
                  value={regForm.playerNames}
                  onChange={e => setRegForm({ ...regForm, playerNames: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #D1D5DB', fontSize: '0.88rem', fontWeight: '500', outline: 'none', resize: 'vertical' }}
                />
              </div>

              {/* PAYMENT & FEE BREAKDOWN BOX */}
              <div style={{ background: '#F8FAF7', padding: '1.25rem', borderRadius: '16px', border: '1.5px solid #EEF2E6' }}>
                <div style={{ fontSize: '0.78rem', fontWeight: '800', color: '#084734', textTransform: 'uppercase', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <ShieldCheck size={16} /> Payment & Fee Summary
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#4B5563', marginBottom: '6px' }}>
                  <span>Tournament Entry Fee:</span>
                  <strong style={{ color: '#111827' }}>₹{feeBreakdown ? feeBreakdown.entry_fee : registeringTournament.entry_fee}</strong>
                </div>

                {feeBreakdown && feeBreakdown.customer_fee > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem', color: '#4B5563', marginBottom: '6px' }}>
                    <span>Platform Service Fee:</span>
                    <strong style={{ color: '#084734' }}>+₹{feeBreakdown.customer_fee}</strong>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #E5E7EB', paddingTop: '8px', marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.95rem', fontWeight: '900', color: '#111827' }}>Total Payable:</span>
                  <span style={{ fontSize: '1.4rem', fontWeight: '900', color: '#084734' }}>
                    ₹{feeBreakdown ? feeBreakdown.total_payable : registeringTournament.entry_fee}
                  </span>
                </div>
              </div>

              {/* SUBMIT PAYU BUTTON */}
              <button
                type="submit"
                disabled={submittingReg}
                style={{
                  background: '#084734', color: '#CEF17B', border: 'none',
                  padding: '14px 24px', borderRadius: '14px', fontWeight: '800',
                  fontSize: '1rem', cursor: submittingReg ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  boxShadow: '0 8px 24px rgba(8,71,52,0.25)', marginTop: '6px'
                }}
              >
                <CreditCard size={20} />
                {submittingReg ? 'Processing Checkout...' : (feeBreakdown && feeBreakdown.total_payable > 0 ? `Pay Entry Fee (₹${feeBreakdown.total_payable}) via PayU →` : 'Register Free Team →')}
              </button>
            </form>

          </div>
        </div>
      )}
    </div>
  );
}
